/* eslint-disable react-hooks/rules-of-hooks */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Filter from '@/pages/dashboard/components/NewFilter';
import { setBtnPointLog } from '@/pages/dashboard/utils/utils';
import { renderCategory, renderLanguage } from './FilterList';
import { useFilterData } from './hooks';
import { FORM_MAP } from '../../utils/_config/constant';
import { useIntl, useSelector } from 'umi';
import { firstWordUpper } from './_utils';
import { commonDateDefault } from '../../utils/dateUtils';
import { getFilterSelector } from '../../components/NewFilter/util';
import { getCustomFilterList } from '../../components/NewFilter/customFilterList';
import { filterValueType } from '../../components/NewFilter/_type';
import { isEqual } from 'lodash-es';

interface ICatalogData {
  values?: string[];
  gameSelectedInfo?: Record<string, any> | null;
  list?: Array<{ value: string; image?: string; }>;
}

interface ISalesOverviewFilterProps {
  filterConfirm: (obj: Record<string, any>) => void;
  filterReset: (obj: Record<string, any>) => void;
  filterInit: (obj: Record<string, any>) => void;
  buttonLogConfig?: Record<string, any>;
  isHaveFilterSave?: boolean;
  openSticky?: boolean;
  path: string;
  children?: React.ReactNode;
  timeZone?: number;
  config?: {
    selectorConfig?: {
      api?: string;
      otherParams?: Record<string, any>;
    };
    systemCascadeBoxClassName?: string;
  };
  data?: {
    catalog?: ICatalogData;
  };
}

const getCatalogPayload = (catalog?: ICatalogData): Record<string, any> => {
  if (!catalog) return {};
  return {
    catalog: catalog.values ?? catalog,
    filterCatalog: catalog.gameSelectedInfo || null,
  };
};

const defaultSelector = [{
  key: 'region',
  dataKey: 'region',
  default: {
    region_type: 'market',
    markets: ["255"],
    regions: []
  },
  name_cn: '区域',
  name_en: 'Region',
},
{
  key: 'system',
  dataKey: 'system',
  default: ["255"],
  name_cn: '平台',
  name_en: 'Platform',
  view_type: "group"
},
{
  key: 'category',
  dataKey: 'category',
  default: {
    category_type: 'product',
    product: ["255"],
    category: ["255"]
  },
  name_cn: '产品',
  name_en: 'Product',
  render: renderCategory
}, 
{
  key: 'zone',
  dataKey: 'zone',
  visible: 0,
  name_cn: 'Zone',
  name_en: '区服',
  default: ["255"],
},
{
  key: 'channel',
  dataKey: 'channel',
  visible: 0,
  default: ["255"],
  name_cn: '渠道',
  name_en: 'Channel',
},
{
  key: 'lang',
  dataKey: 'lang',
  visible: 0,
  default: ["255"],
  name_cn: '语言',
  name_en: 'Language',
  render: renderLanguage,
}
];

const SalesOverviewFilter: React.FC<ISalesOverviewFilterProps> = (props) => {
  const { filterConfirm, filterReset, filterInit, buttonLogConfig, isHaveFilterSave, openSticky = false, path, children, timeZone = 0, config, data } = props;
  const [filterValue, setFilterValue] = useState<any>(null);
  // const { loading, selector } = useFilterData(path, defaultSelector);
  const { locale, formatMessage } = useIntl();
  const { selectorConfig, systemCascadeBoxClassName } = config || {};
  const { loading, selector } = useFilterData(path, defaultSelector, selectorConfig?.api, selectorConfig?.otherParams);
  const customFilterList = getCustomFilterList();

  const prevCatalogRef = useRef(data?.catalog?.values ?? data?.catalog);
  const isInitializedRef = useRef(false);
  const filterConfirmRef = useRef(filterConfirm);
  filterConfirmRef.current = filterConfirm;

  useEffect(() => {
    const catalogObj = data?.catalog;
    const newCatalogValues = catalogObj?.values ?? catalogObj;
    if (isInitializedRef.current && newCatalogValues !== undefined && !isEqual(newCatalogValues, prevCatalogRef.current)) {
      prevCatalogRef.current = newCatalogValues;
      filterConfirmRef.current(getCatalogPayload(catalogObj));
    }
  }, [data?.catalog]);
  
  let realPath = path;
  if (!realPath.startsWith("/")) {
    realPath = `/${realPath}`;
  }
  const { latestDate } = selector || {};
  const { seasonInfo, gameConfig } = useSelector((state: any) => 
    // 初始可能为undefied,在此加兜底
    ({
      seasonInfo: state?.dashboardSetting?.gameConfig?.season_info || { date: [], week: [], month: [] },
      gameConfig: state.dashboardSetting.gameConfig,
    })
  );
    // 页面筛选器配置
  const defaultSelectArr = gameConfig?.page_selector?.find((item: any) => item?.path === realPath);
  let defaultSelect = defaultSelectArr?.filters;
  defaultSelect = getFilterSelector(gameConfig?.page_selector, defaultSelectArr, realPath?.trim()?.toLowerCase())?.filters;

  const customFilter = useMemo(()=>{
    return customFilterList?.map((item: string) => {
      const input = defaultSelect?.find((v: any) => v?.key === item);
      return {
        dataKey: input?.key,
        ...input
      };
    });
  },[customFilterList, defaultSelect]);

  const defaultInputList = useMemo(()=>[
    commonDateDefault(timeZone, seasonInfo),
    ...defaultSelector,
    ...(customFilter || {})
  ],[seasonInfo, timeZone, customFilter]);

  // 设置inputList
  const inputList = useMemo(() => {
    if (loading) return [];
    // 后端未配置使用默认的筛选器
    if (!defaultSelect) return defaultInputList;
    // 筛选器私有化的处理显示隐藏
    const list: any = [];
    defaultInputList?.map((item: any) => {
      const input = defaultSelect?.find((v: any) => v?.key === item?.dataKey);
      if (item?.key === 'rangeTime') {
        list?.push(item);
      } else if (input && input?.visible !== 0) {
        if (input?.key === 'region') {
          list?.push({
            ...item, default: {
              markets: input?.default,
              region_type: "market",
              regions: [],
            },
            name_cn: input?.name_cn || item?.name_cn,
            name_en: input?.name_en || item?.name_en,
          });
        } else if (input?.key === 'category') {
          const productValue = defaultSelect?.find((v: any) => (v?.key === 'category' && v?.visible !== 0));
          list?.push({
            ...item, default: {
              product: productValue?.default ?? ['255'],
              category_type: "product",
              category: input?.default ?? ['255'],
            },
            name_cn: input?.name_cn || item?.name_cn,
            name_en: input?.name_en || item?.name_en,
          });
        } else {
          if(item?.key === 'payment_type') {
            list?.push({ ...item, default: input?.default, visible: input?.visible,
              name_cn: input?.name_cn || item?.name_cn,
              name_en: input?.name_en || item?.name_en,
              tooltip: formatMessage({id: 'common_filter_payment_type_tooltip'}),
              ...(input?.view_type ? { view_type: input.view_type } : {}),
            });
          } else {
            list?.push({ ...item, default: input?.default, visible: input?.visible,
              name_cn: input?.name_cn || item?.name_cn,
              name_en: input?.name_en || item?.name_en,
              ...(input?.view_type ? { view_type: input.view_type } : {}),
              ...(item?.key === 'system' && systemCascadeBoxClassName ? { cascadeBoxClassName: systemCascadeBoxClassName } : {}),
            });
          }
        
        }
      }
    });
    return list;
  }, [loading, defaultSelect, locale, defaultInputList]);

  // 筛选器私有化的处理隐藏筛选器的值
  const noVisibleValue = useMemo(() => {
    const obj: any = {};
    const keyMap: any = {
    };
    defaultSelect?.map((item: any) => {
      if (item?.visible === 0) {
        if (item?.key === 'region') {
          obj[keyMap?.[item?.key] || item?.key] = {
            markets: item?.default,
            region_type: "market",
            regions: []
          };
        } else if (item?.key === 'category') {
          const productValue = defaultSelect?.find((v: any) => (v?.key === 'category' && v?.visible === 0));
          obj[keyMap?.[item?.key] || item?.key] = {
            product: productValue?.default ?? ['255'],
            category_type: "product",
            category: item?.default ?? ['255'],
          };
        } else {
          obj[keyMap?.[item?.key] || item?.key] = item?.default;
        }
      }
    });
    return obj;
  }, [defaultSelect]);

  // 私有化处理，抹平新旧筛选器的差距
  const onInit = useCallback((value: { rangeTime: { value: { format: (arg0: string) => any; }[]; type: string; }; }, isHaveExtendFIlter: any) => {
    setFilterValue({ ...value, ...noVisibleValue });
    // 标记初始化完成
    isInitializedRef.current = true;
    // 记录初始 catalog 值
    prevCatalogRef.current = data?.catalog?.values ?? data?.catalog;
    // todo 锁定状态下如果锁定时间跟默认时间相同，是否需要单独处理，设置diff为false？
    // const formattedValue = formatDateFromFilterValue({ ...value, ...noVisibleValue });
    filterInit({
      ...value,
      ...noVisibleValue,
      rangeTime: [
        value?.rangeTime?.value?.[0]?.format('YYYY-MM-DD'),
        value?.rangeTime?.value?.[1]?.format('YYYY-MM-DD')
      ],
      timeType: firstWordUpper(value?.rangeTime?.type),
      // newFilter: true,
      selector: selector,
      [FORM_MAP.RANGE_TIME_DIFF]: !isHaveExtendFIlter,
      pagePath: path,
      filterDefaultSelect: defaultSelector,
      ...getCatalogPayload(data?.catalog),
    });
  }, [filterInit, noVisibleValue, selector, data?.catalog]);

  const onConfirm = useCallback((value: { rangeTime: { value: { format: (arg0: string) => any; }[]; type: string; }; }) => {
    setFilterValue({ ...value, ...noVisibleValue });
    // const formattedValue = formatDateFromFilterValue({ ...value, ...noVisibleValue });
    const obj: any = {
      ...value,
      ...noVisibleValue,
      rangeTime: [
        value?.rangeTime?.value?.[0]?.format('YYYY-MM-DD'),
        value?.rangeTime?.value?.[1]?.format('YYYY-MM-DD')
      ],
      timeType: firstWordUpper(value?.rangeTime?.type),
      // newFilter: true,
      selector: selector,
      [FORM_MAP.RANGE_TIME_DIFF]: false,
      pagePath: path,
      filterDefaultSelect: defaultSelector,
      ...getCatalogPayload(data?.catalog),
    };
    filterConfirm(obj);
    // 打点，沟通后打点操作放在业务代码的confirm和reset中，不封装在新版本筛选器中
    setBtnPointLog(buttonLogConfig, {
      ...value,
      rangeTime: [
        value?.rangeTime?.value?.[0]?.format('YYYY-MM-DD'),
        value?.rangeTime?.value?.[1]?.format('YYYY-MM-DD')
      ],
      timeType: firstWordUpper(value?.rangeTime?.type),
      ...getCatalogPayload(data?.catalog),
    });
  }, [filterConfirm, selector, data?.catalog]);

  const onReset = useCallback((value: { rangeTime: { value: { format: (arg0: string) => any; }[]; type: string; }; }) => {
    setFilterValue({ ...value, ...noVisibleValue });
    // const formattedValue = formatDateFromFilterValue({ ...value, ...noVisibleValue });
    const obj = {
      ...value,
      ...noVisibleValue,
      rangeTime: [
        value?.rangeTime?.value?.[0]?.format('YYYY-MM-DD'),
        value?.rangeTime?.value?.[1]?.format('YYYY-MM-DD')
      ],
      timeType: firstWordUpper(value?.rangeTime?.type),
      // newFilter: true,
      selector: selector,
      [FORM_MAP.RANGE_TIME_DIFF]: true,
      pagePath: path,
      filterDefaultSelect: defaultSelector,
      ...getCatalogPayload(data?.catalog),
    };
    filterReset(obj);
    // 打点，沟通后打点操作放在业务代码的confirm和reset中，不封装在新版本筛选器中
    setBtnPointLog(buttonLogConfig, {
      ...value,
      rangeTime: [
        value?.rangeTime?.value?.[0]?.format('YYYY-MM-DD'),
        value?.rangeTime?.value?.[1]?.format('YYYY-MM-DD')
      ],
      timeType: firstWordUpper(value?.rangeTime?.type),
      ...getCatalogPayload(data?.catalog),
    }, true);
  }, [filterReset, selector, data?.catalog]);

  const handleChange = (key: string, value: any,formVal: filterValueType) => {
    setFilterValue({...formVal, [key]: value,...noVisibleValue});
  };

  return (
    <Filter
      openSticky={openSticky}
      path={path}
      isHaveFilterSave={isHaveFilterSave}
      outLatestDate={latestDate}
      value={filterValue}
      latestDateType={''}
      inputList={inputList}
      onInit={onInit}
      onConfirm={onConfirm}
      onReset={onReset}
      onChange={handleChange}
      // isHaveFilterCache={false}
      loading={loading}
      useOutInput={true}
      outSelector={selector}
    >
      {children}
    </Filter>
    
  );
};

export default SalesOverviewFilter;
